import {
    useCallback, useMemo, useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { ContactGroupEnum } from 'components/Connections/types';
import { useGetCustomService } from 'hooks/rest/useGetCustomService';
import useClientProfile from 'prodivers/clientProfile/hooks/useClientProfile';
import { useGetContactGroups } from 'hooks/rest/useGetContactGroups';
import { useOptionList } from 'hooks/useOptionList';
import { OPTION_LIST, PRODUCTS } from 'constants/constants';
import { getRestrictedClientMessage } from 'widgets/ProductOffers/utils';
import { useGetCheckIfParentalAuthority } from 'hooks/rest/useGetCheckIfParentalAuthority';
import useLayoutController from 'components/Stepper/RouteBasedStepper/useLayoutController';
import { useCreateProposal } from '../CreateProposalManager';

export const useProposalProductSelection = () => {
    const { ...information }: any = useClientProfile();
    const { clientId } = useClientProfile();
    const { t } = useTranslation();

    const {
        currentClientSelector,
        selectedProductTypeSelector,
        is3bProductGroupSelector,
        selectedProductGroupIdSelector,
        isLoadingProducts,
        groupedProducts,
        errorProducts,
        selectedProposalOwner,
        setSelectedProposalOwnerAction,
        setSelectedProductIdAction,
        setConnectionGroupIdAction,
    } = useCreateProposal();

    const [showThreeBGroupsOrMortgageModal, setShowThreeBGroupsOrMortgageModal] = useState<string | undefined>(undefined);
    const [saving, setSaving] = useState(false);
    const [selectedProposalOwnerId, setSelectedProposalOwnerId] = useState<string | number | undefined>(undefined);

    const { data: contactGroups, refetch: contactGroupsRefetch } = useGetContactGroups({
        contactId: clientId,
        isEnabled: false,
        createThreeB: true,
        createMortgage: false,
    });

    const { lists: [fatfGreylist, countries] } = useOptionList([OPTION_LIST.fatfGreylist, OPTION_LIST.country]);

    const { data: isFzAndFzpAlreadyAppliedData } = useGetCustomService({
        service: 'customInvestmentService',
        api: 'checkIsFzAndFzpEnabled',
        payload: clientId,
        enabled: !!clientId,
    });

    const isFzAndFzpAlreadyApplied = useMemo(() => isFzAndFzpAlreadyAppliedData?.data, [isFzAndFzpAlreadyAppliedData?.data]);

    const clientFitFailMessage = useMemo(
        () => getRestrictedClientMessage({ data: currentClientSelector, selectedProductType: selectedProductTypeSelector, isMortgage: false }),
        [currentClientSelector, selectedProductTypeSelector],
    );

    const {
        data: isParentalAuthorityAvailable,
        isLoading: isParentalAuthorityLoading,
        error: getParentalAuthorityErrors,
    } = useGetCheckIfParentalAuthority({
        clientId,
        isEnabled: (is3bProductGroupSelector && currentClientSelector && currentClientSelector.age < 18 && !clientFitFailMessage),
    });

    const parentalAuthorityErrorMessage = useMemo(() => (
        (isParentalAuthorityAvailable === false) ? 'portfolio.createNewPortfolio.3bParentalAuthorityRequired' : undefined
    ), [isParentalAuthorityAvailable]);

    const nationalityValidation = useMemo(() => {
        const countryOfNationality = countries?.find(f => f.id === currentClientSelector?.nationality);

        if (fatfGreylist?.some(s => s.label === countryOfNationality?.label && (is3bProductGroupSelector))) {
            return 'portfolio.createNewPortfolio.nationalityOnFatfGrayList';
        }

        return null;
    }, [countries, fatfGreylist, is3bProductGroupSelector, currentClientSelector]);

    const infoLabelData = useMemo(() => {
        if (!!clientFitFailMessage || !!parentalAuthorityErrorMessage || !!nationalityValidation) {
            return {
                isError: !!clientFitFailMessage,
                message: clientFitFailMessage || parentalAuthorityErrorMessage || nationalityValidation,
            };
        }

        return null;
    }, [clientFitFailMessage, parentalAuthorityErrorMessage, nationalityValidation]);

    const isLoading = useMemo(() => (information?.isLoading || isLoadingProducts), [information?.isLoading || isLoadingProducts]);
    const pageErrors = useMemo(() => (errorProducts || getParentalAuthorityErrors || undefined), [errorProducts || getParentalAuthorityErrors]);
    const continueInProgress = useMemo(() => (isParentalAuthorityLoading || saving), [isParentalAuthorityLoading || saving]);
    const continueDisabled = useMemo(
        () => (!selectedProductGroupIdSelector || selectedProductGroupIdSelector === -1 || !!clientFitFailMessage || continueInProgress || isLoading || !!pageErrors),
        [selectedProductGroupIdSelector, clientFitFailMessage, continueInProgress, pageErrors, isLoading],
    );

    const onThreeBConnectionGroupSelected = useCallback((groupId: string) => {
        setConnectionGroupIdAction(groupId);
        const selectedContactGroup = contactGroups?.find(group => group.groupId === groupId);
        const selectedClient = selectedProposalOwnerId ? {
            id: selectedProposalOwnerId,
            name: selectedContactGroup?.owners?.[selectedProposalOwnerId],
        } : {
            id: clientId,
            name: selectedContactGroup?.owners?.[clientId as number],
        };

        setSelectedProposalOwnerAction(selectedClient);
        setStepComplete(true);
    }, [selectedProposalOwnerId, contactGroups, clientId]);

    const handle3bSelectedProduct = useCallback(async ({ type }) => {
        const { data } = await contactGroupsRefetch();

        const filteredData = data?.filter(dta => dta?.groupName !== ContactGroupEnum.mortgage);

        if (filteredData?.length > 1) {
            setShowThreeBGroupsOrMortgageModal(type);
        } else {
            onThreeBConnectionGroupSelected(filteredData?.[0]?.groupId);
        }
    }, [onThreeBConnectionGroupSelected]);

    const onContinue = useCallback(async () => {
        setSaving(true);
        if (is3bProductGroupSelector) {
            await handle3bSelectedProduct({ type: PRODUCTS.bbb });
        } else {
            setStepComplete(true);
        }
        setSaving(false);
    }, [is3bProductGroupSelector, handle3bSelectedProduct]);

    const { setStepComplete } = useLayoutController({
        callbackOnContinue: onContinue,
        isContinueDisabled: continueDisabled || isLoading || continueInProgress || pageErrors?.length > 0,
        isBackDisabled: true,
        reSubscriptionStates: [is3bProductGroupSelector],
    });

    return {
        clientId,
        isLoading,
        pageErrors,
        groupedProducts,
        isFzAndFzpAlreadyApplied,
        infoLabelData,
        onContinue,
        setSelectedProductId: setSelectedProductIdAction,
        selectedProductId: selectedProductGroupIdSelector,
        onThreeBConnectionGroupSelected,
        contactGroups,
        showThreeBGroupsOrMortgageModal,
        setShowThreeBGroupsOrMortgageModal,
        selectedProposalOwner,
        threeBConnectionModalTitle: t('productOffer.3bGroup.selectProductOwner.title'),
        threeBConnectionModalSubtitle: '',
        onChangeMainClient: (_clientId) => setSelectedProposalOwnerId(parseInt(_clientId, 10)),
    };
};
